import React, { Suspense, lazy, ComponentType } from "react";

// Loading component for better UX
const LoadingSpinner: React.FC = () => (
  <div className="flex justify-center items-center min-h-[200px]">
    <div className="spinner"></div>
  </div>
);

// Detect if we are running in react-snap or hydration mode
const isReactSnap =
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";
if (isReactSnap) {
  /* no-op for lint */
}

// Helper function to retry lazy loading
const lazyWithRetry = <P extends Record<string, any>>(
  componentImport: () => Promise<{ default: ComponentType<P> }>,
  retriesLeft = 3,
  interval = 1000,
): Promise<{ default: ComponentType<P> }> => {
  return new Promise((resolve, reject) => {
    componentImport()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          lazyWithRetry(componentImport, retriesLeft - 1, interval).then(
            resolve,
            reject,
          );
        }, interval);
      });
  });
};

// Higher-order component for lazy loading
export const withLazyLoading = <P extends Record<string, any>>(
  importFunc: () => Promise<{ default: ComponentType<P> }>,
  fallback?: React.ComponentType,
) => {
  const LazyComponent = lazy(() => lazyWithRetry(importFunc));

  const LazyWrapper = (props: P) => {
    // During react-snap pre-rendering, we want to wait for the component
    // In React 18, lazy components work fine with hydration if Suspense fallback matches

    return (
      <Suspense
        fallback={
          fallback ? (
            React.createElement(fallback)
          ) : isReactSnap ? null : (
            <LoadingSpinner />
          )
        }
      >
        <LazyComponent {...(props as any)} />
      </Suspense>
    );
  };

  LazyWrapper.displayName = "LazyWrapper";
  return LazyWrapper;
};

// Lazy load components that are not critical for initial render
export const LazyAdminPanel = withLazyLoading(() => import("./AdminPanel"));
export const LazyBlogAdmin = withLazyLoading(() => import("./BlogAdmin"));
export const LazyBlogPostEditor = withLazyLoading(
  () => import("./BlogPostEditor"),
);
export const LazyBlogPostDetail = withLazyLoading(
  () => import("./BlogPostDetail"),
);
export const LazyCategoryArchive = withLazyLoading(
  () => import("./CategoryArchive"),
);
export const LazyTagArchive = withLazyLoading(() => import("./TagArchive"));
export const LazyTagManager = withLazyLoading(() => import("./TagManager"));
export const LazyCategoryManager = withLazyLoading(
  () => import("./CategoryManager"),
);
export const LazyWhatsAppAdmin = withLazyLoading(
  () => import("./WhatsAppAdmin"),
);
export const LazyAffiliateManager = withLazyLoading(
  () => import("./AffiliateManager"),
);
export const LazyConversionManager = withLazyLoading(
  () => import("./ConversionManager"),
);
export const LazyTicketManager = withLazyLoading(
  () => import("./TicketManager"),
);
export const LazyTicketConversation = withLazyLoading(
  () => import("./TicketConversation"),
);
export const LazyCreateTicket = withLazyLoading(() => import("./CreateTicket"));
export const LazyTicketLookup = withLazyLoading(() => import("./TicketLookup"));
export const LazyAffiliatePortal = withLazyLoading(
  () => import("./AffiliatePortal"),
);
export const LazyBecomeAffiliate = withLazyLoading(
  () => import("./BecomeAffiliate"),
);
export const LazyPasswordResetPage = withLazyLoading(
  () => import("./PasswordResetPage"),
);
export const LazyGlowingButtonDemo = withLazyLoading(
  () => import("./GlowingButtonDemo"),
);

// Service pages - lazy load as they're not critical for initial render
export const LazyLawFirmsPage = withLazyLoading(() => import("./LawFirmsPage"));
export const LazyRealEstatePage = withLazyLoading(
  () => import("./RealEstatePage"),
);
export const LazyNonprofitPage = withLazyLoading(
  () => import("./NonprofitPage"),
);
export const LazyIndustrialPage = withLazyLoading(
  () => import("./IndustrialPage"),
);
export const LazyHomeServicesPage = withLazyLoading(
  () => import("./HomeServicesPage"),
);
export const LazyContactPage = withLazyLoading(() => import("./ContactPage"));
export const LazyWebsiteDesignPage = withLazyLoading(
  () => import("./WebsiteDesignPage"),
);
export const LazyShopifyDevelopmentPage = withLazyLoading(
  () => import("./ShopifyDevelopmentPage"),
);
export const LazyWebsiteDevelopmentPage = withLazyLoading(
  () => import("./WebsiteDevelopmentPage"),
);
export const LazyEcommercePage = withLazyLoading(
  () => import("./EcommercePage"),
);
export const LazyMVPDevelopmentPage = withLazyLoading(
  () => import("./MVPDevelopmentPage"),
);
export const LazyMERNStackPage = withLazyLoading(
  () => import("./MERNStackPage"),
);
export const LazyPriceComparisonPage = withLazyLoading(
  () => import("./PriceComparisonPage"),
);
export const LazyClientsPage = withLazyLoading(() => import("./ClientsPage"));
export const LazyProcessPage = withLazyLoading(() => import("./ProcessPage"));
export const LazyPricingPage = withLazyLoading(() => import("./PricingPage"));
export const LazyBlogPage = withLazyLoading(() => import("./BlogPage"));
export const LazyPfrServicePage = withLazyLoading(
  () => import("./PfrServicePage"),
);
export const LazySpeedOptimizationPage = withLazyLoading(
  () => import("./SpeedOptimizationPage"),
);
export const LazyGEOExpertPage = withLazyLoading(
  () => import("./GEOExpertPage"),
);
export const LazyHeadlessWordpressPage = withLazyLoading(
  () => import("./HeadlessWordpressPage"),
);
export const LazyWordPressMaintenancePage = withLazyLoading(
  () => import("./WordPressMaintenancePage"),
);
export const LazyHireWordpressDevelopersPage = withLazyLoading(
  () => import("./HireWordpressDevelopersPage"),
);
export const LazyWoocommerceSubscriptionPage = withLazyLoading(
  () => import("./WoocommerceSubscriptionPage"),
);
export const LazyMoizBookingProPage = withLazyLoading(
  () => import("./MoizBookingProPage"),
);
export const LazyKnowledgeBasePage = withLazyLoading(
  () => import("./KnowledgeBasePage"),
);
export const LazyMbpKnowledgeBasePage = withLazyLoading(
  () => import("./MbpKnowledgeBasePage"),
);
export const LazyAffiliateMarketingEnginePage = withLazyLoading(
  () => import("./AffiliateMarketingEnginePage"),
);
export const LazyMoizWpCrmPage = withLazyLoading(
  () => import("./MoizWpCrmPage"),
);
export const LazyPriceComparisonTeaser = withLazyLoading(
  () => import("./PriceComparisonTeaser"),
);
export const LazyWhatsappIntakeAutomationPage = withLazyLoading(
  () => import("./WhatsappIntakeAutomationPage"),
);
export const LazyWorkflowAutomationsPage = withLazyLoading(
  () => import("./WorkflowAutomationsPage"),
);
export const LazyLinkedinOutreachPage = withLazyLoading(
  () => import("./LinkedinOutreachPage"),
);
export const LazyAiAgentTrackerPage = withLazyLoading(
  () => import("./AiAgentTrackerPage"),
);
export const LazyEhmsDecisionStudioPage = withLazyLoading(
  () => import("./EhmsDecisionStudioPage"),
);
export const LazySitemapPage = withLazyLoading(() => import("./SitemapPage"));
export const LazyPrivacyPolicyPage = withLazyLoading(
  () => import("./PrivacyPolicyPage"),
);
export const LazyTermsOfUsePage = withLazyLoading(
  () => import("./TermsOfUsePage"),
);

// Heavy components that can be lazy loaded
export const LazyPortfolio = withLazyLoading(() => import("./Portfolio"));
export const LazyTestimonials = withLazyLoading(() => import("./Testimonials"));
export const LazyAssessmentSection = withLazyLoading(
  () => import("./AssessmentSection"),
);
export const LazyClients = withLazyLoading(() => import("./Clients"));
export const LazyPricing = withLazyLoading(() => import("./Pricing"));
export const LazyAdminLogin = withLazyLoading(() => import("./AdminLogin"));
export const LazyPricingPlans = withLazyLoading(() => import("./PricingPlans"));
